/**
 * Shared shift-start anchor (#147 → #148).
 *
 * The operator's shift-start timestamp is the single time source for both the
 * Anomaly Queue's "only new since shift" view (#147) and the Shift Log feed
 * (#148). It lives in localStorage so it survives a reload within a shift, and is
 * broadcast on a custom event so every consumer (queue + shift log) re-renders in
 * lockstep when one of them resets it.
 */
import { useEffect, useState } from 'react';

/** localStorage key for the operator's shift-start anchor (epoch ms, as string). */
export const SHIFT_START_KEY = 'tsr-anomaly-shift-start';
/** Same-document broadcast so sibling components sync without a storage event
 *  (which only fires across tabs). */
const SHIFT_START_EVENT = 'tsr-shift-start-changed';

function readStored(): number | null {
  try {
    const raw = localStorage.getItem(SHIFT_START_KEY);
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n)) return n;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeStored(ms: number): void {
  try {
    localStorage.setItem(SHIFT_START_KEY, String(ms));
  } catch {
    /* ignore */
  }
}

/** Read the current anchor, initialising + persisting it to now on first use. */
export function getShiftStart(): number {
  const stored = readStored();
  if (stored !== null) return stored;
  const now = Date.now();
  writeStored(now);
  return now;
}

/** Local HH:MM label for the anchor. */
export function clockLabel(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Subscribe to the shared shift-start anchor. Returns the current value and a
 * `reset()` that re-anchors to now and broadcasts to every other consumer.
 */
export function useShiftStart(): { shiftStart: number; reset: () => void } {
  const [shiftStart, setShiftStart] = useState<number>(() => getShiftStart());

  useEffect(() => {
    const sync = (): void => {
      const v = readStored();
      if (v !== null) setShiftStart(v);
    };
    window.addEventListener(SHIFT_START_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SHIFT_START_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const reset = (): void => {
    const now = Date.now();
    writeStored(now);
    setShiftStart(now);
    window.dispatchEvent(new Event(SHIFT_START_EVENT));
  };

  return { shiftStart, reset };
}
